import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Icon } from '@/components/Icon'
import { ZodiacIcon, type ZodiacName } from '@/components/ZodiacIcon'
import { MagneticButton } from '@/components/MagneticButton'
import { RequestModal } from '@/components/RequestModal'
import { PlaceholderEvents } from '@/components/PlaceholderEvents'
import { placeholderEventsFor } from '@/data/placeholderEvents'
import { SiteFooter, VideoPanel, PartnerCallout } from '@/components/ui/hero-sections'
import { SiteNav } from '@/components/ui/site-chrome'
import { Modal, Chip, EventRow, ParticipationNote } from '@/components/lc'
import { useSmoothScroll } from '@/lib/useSmoothScroll'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'
import { communityWebappUrl, communityHomeUrl, consultWebappUrl, eventWebappUrl } from '@/lib/auth'
import { useCommunityCircles } from '@/lib/circles'
import { useLifecrafterIndex, lookupLifecrafterId } from '@/lib/lifecrafters'
import { useCommunityEvents } from '@/lib/events'
import { cn } from '@/lib/utils'
import { cosmos, cosmosDaily, cosmosConsult, cosmosMentors, cosmosMarketplace, cosmosSubgenres, cosmosConsultCrafters, cosmosConsultTiers, cosmosCircles } from '@/data/content'
import type { DailyBlock, ConsultCrafter } from '@/data/content'
import { useSheet, sheetConfigured, type SheetRow } from '@/lib/sheet'
import {
  zodiacSigns,
  muhuratCategories,
  demoDailyHoroscope,
  demoYearly2026,
  demoMuhurat,
  type MuhuratCategory,
} from '@/data/cosmosSheet'

/* ================================================================== */
/*  Cosmos community page, aligned to the client's category board:     */
/*  video hero with three CTAs, a daily-engagement layer of clickable  */
/*  blocks (modals), sub-genre cards, experts with consultations, and  */
/*  events with member/subscriber pricing.                             */
/* ================================================================== */

const glass = (_dark: boolean) => 'lc-glass'
const glassSoft = (_dark: boolean) => 'lc-glass-soft'
const inkText = (_dark: boolean) => 'text-lc-ink'
const muteText = (_dark: boolean) => 'text-lc-mute'

/* full-bleed rail padding: aligns the first card with the page content edge
   while letting cards scroll to the true viewport edge */
const RAIL_EDGE = 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))'

// Cosmos mentors that book straight into the 1-1 page (exactly like the
// Premium / Elite "Book" CTA) instead of the "Price on request" enquiry. All
// other mentors keep price-on-request. These crafters have priced 1-1 programs
// in the webapp; Poonam & Vivek are also removed from the Premium lane so they
// aren't duplicated.
const MENTOR_BOOK_NAMES = new Set<string>([
  'Naresh Kumar Sharma',
  'Vivek Kush',
  'Alok Gautam',
  'Acharya Poonam Gaur',
  'Dr. Minaakshi Rathour',
])

// The Daily / Weekend consultations are run by the "Cosmos Program Hub"
// LifeCrafter (name "LifeCraft Coordinator", alias "Cosmos Program Hub"), so
// their Book CTA deep-links into that crafter's 1-1 booking. We prefer the id
// resolved live from the feed, but hardcode it as a fallback because the public
// feed is CORS-blocked in the browser today.
const COSMOS_PROGRAM_HUB_NAME = 'Cosmos Program Hub'
const COSMOS_PROGRAM_HUB_ID = '6aa6b73d8fd368597b3ea5d9'

function Backdrop({ dark }: { dark: boolean }) {
  const reduced = usePrefersReducedMotion()
  const blobs = dark
    ? [
        { cls: 'h-[50rem] w-[50rem] -left-56 top-24 opacity-45', color: '#7C3AED', d: 26 },
        { cls: 'h-[42rem] w-[42rem] -right-40 top-[16%] opacity-45', color: '#FF6B5C', d: 33 },
        { cls: 'h-[46rem] w-[46rem] left-[20%] top-[56%] opacity-45', color: '#5B21B6', d: 39 },
      ]
    : [
        { cls: 'h-[46rem] w-[46rem] -left-48 -top-48 opacity-50', color: '#FFD3EF', d: 27 },
        { cls: 'h-[40rem] w-[40rem] -right-36 top-[14%] opacity-45', color: '#D9D2FF', d: 34 },
        { cls: 'h-[42rem] w-[42rem] left-[22%] top-[58%] opacity-40', color: '#FFE4C8', d: 40 },
      ]
  return (
    <div className={cn('pointer-events-none fixed inset-0 z-0 overflow-hidden grain', dark && 'grain-on-dark')} aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{ background: dark ? 'linear-gradient(180deg, #191231 0%, #130E22 55%, #170F2A 100%)' : 'linear-gradient(180deg, #FAF6EE 0%, #F4EEE2 55%, #F7F1E6 100%)' }}
      />
      {blobs.map((b, i) => (
        <motion.div
          key={i}
          className={cn('blob', b.cls)}
          style={{ background: b.color }}
          animate={reduced ? undefined : { x: [0, 60 - i * 30, 0], y: [0, 40 + i * 10, 0] }}
          transition={reduced ? undefined : { duration: b.d, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
      {dark && (
        <>
          <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 38%, transparent 42%, rgba(8,6,16,0.72) 100%)' }} />
          {/* phones: the blurred blobs bloom brighter on a small canvas, washing
              the bg pink and killing text contrast — darker scrim on mobile only */}
          <div className="absolute inset-0 md:hidden" style={{ background: 'rgba(9,6,18,0.5)' }} />
        </>
      )}
    </div>
  )
}

/* ---- drifting star sparkles for the hero sky -------------------------- */
const SPARKS = [
  { x: 8, y: 18, s: 14, d: 0 }, { x: 22, y: 64, s: 10, d: 1.2 }, { x: 38, y: 30, s: 12, d: 2.1 },
  { x: 55, y: 74, s: 9, d: 0.6 }, { x: 64, y: 22, s: 16, d: 1.7 }, { x: 78, y: 58, s: 11, d: 0.3 },
  { x: 90, y: 30, s: 13, d: 2.5 }, { x: 46, y: 10, s: 9, d: 1.0 }, { x: 12, y: 84, s: 12, d: 1.9 },
]
function HeroSparkles() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {SPARKS.map((sp, i) => (
        <span
          key={i}
          className="absolute text-lime motion-safe:animate-float"
          style={{ left: `${sp.x}%`, top: `${sp.y}%`, fontSize: sp.s, animationDelay: `${sp.d}s`, animationDuration: `${5 + (i % 3)}s`, opacity: 0.7 }}
        >
          ✦
        </span>
      ))}
    </div>
  )
}

/* ---- daily-block modal bodies ------------------------------------- */

/* small "join" CTA reused in every modal body */
function JoinCta({ onClose, demo }: { onClose: () => void; demo: boolean }) {
  return (
    <div className="mt-6 flex items-center justify-between gap-4">
      {demo ? <Chip tone="glass" small>Demo content</Chip> : <span />}
      <a
        href={communityHomeUrl('Cosmos') ?? '#join'}
        onClick={onClose}
        className="rounded-pill bg-lime px-5 py-2.5 text-[14px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02]"
      >
        Join the community to experience more
      </a>
    </div>
  )
}

/* today's date as YYYY-MM-DD (local) */
function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function normDate(v: string): string {
  if (!v) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10)
  const t = Date.parse(v)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/* Daily horoscope + Your 2026: pick a sign, read its entry from the sheet. */
function ZodiacModalBody({ block, onClose }: { block: DailyBlock; onClose: () => void }) {
  const yearly = block.kind === 'zodiac-yearly'
  const fallback = yearly ? demoYearly2026 : demoDailyHoroscope
  const { rows } = useSheet(block.sheet ?? '', fallback)
  const [sign, setSign] = useState<string | null>(null)
  const [lang, setLang] = useState<'en' | 'hi'>('en')

  /* pick the right row for a sign: yearly = its single row; daily = today's row,
     else the newest past row, else the soonest upcoming one. */
  const entryFor = (name: string): SheetRow | null => {
    const forSign = rows.filter((r) => (r.sign || '').trim().toLowerCase() === name.toLowerCase())
    if (!forSign.length) return null
    if (yearly) return forSign[0]
    const today = todayISO()
    const exact = forSign.find((r) => normDate(r.date) === today)
    if (exact) return exact
    const dated = forSign
      .map((r) => ({ d: normDate(r.date), r }))
      .filter((x) => x.d)
      .sort((a, b) => (a.d < b.d ? -1 : 1))
    const past = dated.filter((x) => x.d <= today)
    if (past.length) return past[past.length - 1].r
    if (dated.length) return dated[0].r
    return forSign[forSign.length - 1]
  }

  if (sign) {
    const z = zodiacSigns.find((s) => s.name === sign)
    const entry = entryFor(sign)
    const hasHindi = !!entry?.text_hi
    const reading = entry
      ? (lang === 'hi' && entry.text_hi ? entry.text_hi : entry.text || '')
      : 'A reading for this sign is on its way.'
    return (
      <div>
        <button
          onClick={() => setSign(null)}
          className="label inline-flex items-center gap-1.5 !text-[11px] text-lc-mute transition-colors hover:text-lc-ink"
        >
          <span className="transition-transform">←</span> All signs
        </button>
        <div className="mt-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="lc-glass-soft grid h-14 w-14 place-items-center rounded-2xl text-lc-accent">{z && <ZodiacIcon name={z.name as ZodiacName} size={30} />}</span>
            <div>
              <p className="font-display text-2xl text-lc-ink">{z?.name}</p>
              <p className="label !text-[11px] text-lc-mute">{z?.dates}{!yearly ? ' · today' : ' · 2026'}</p>
            </div>
          </div>
          {hasHindi && (
            <div className="lc-glass-soft inline-flex shrink-0 rounded-pill p-0.5 text-[12px] font-bold">
              {(['en', 'hi'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={cn(
                    'rounded-pill px-3 py-1 transition-colors',
                    lang === l ? 'bg-lime text-night' : 'text-lc-mute hover:text-lc-ink',
                  )}
                >
                  {l === 'en' ? 'EN' : 'हिं'}
                </button>
              ))}
            </div>
          )}
        </div>
        <p lang={lang === 'hi' ? 'hi' : 'en'} className="mt-5 whitespace-pre-line text-[15px] leading-relaxed text-lc-mute">{reading}</p>
        <JoinCta onClose={onClose} demo={!sheetConfigured} />
      </div>
    )
  }

  return (
    <div>
      <p className="text-[14px] leading-relaxed text-lc-mute">
        {yearly ? 'Choose your sign for the year ahead.' : 'Choose your sign for today’s reading.'}
      </p>
      <div className="mt-5 grid grid-cols-3 gap-2.5 sm:grid-cols-4">
        {zodiacSigns.map((z) => (
          <button
            key={z.name}
            onClick={() => setSign(z.name)}
            className="lc-glass-soft group flex flex-col items-center gap-1 rounded-2xl p-3 text-center transition-transform duration-300 ease-brand hover:-translate-y-0.5"
          >
            <span className="text-lc-accent transition-transform duration-300 group-hover:scale-110"><ZodiacIcon name={z.name as ZodiacName} size={26} /></span>
            <span className="text-[13px] font-semibold text-lc-ink">{z.name}</span>
            <span className="label !text-[9px] leading-tight text-lc-mute">{z.dates}</span>
          </button>
        ))}
      </div>
      {!sheetConfigured && <div className="mt-6"><Chip tone="glass" small>Demo content</Chip></div>}
    </div>
  )
}

/* Muhurat finder: filter auspicious windows by what you're planning, then by
   month — so a long year of dates reads as a small, chosen slice instead of
   one endless list. */
const MUHURAT_META: Record<string, { ritual: string; blurb: string }> = {
  Car: { ritual: 'Vahan Kraya', blurb: 'Auspicious windows to buy, register or take delivery of a new vehicle.' },
  House: { ritual: 'Griha Pravesh', blurb: 'Griha Pravesh muhurats for moving in and housewarming ceremonies.' },
  Marriage: { ritual: 'Vivah', blurb: 'Vivah muhurats: auspicious days for weddings and engagements.' },
}

/* Small stroked line-icons per category (24×24, currentColor — no emoji). */
function MuhuratIcon({ cat, size = 16 }: { cat: MuhuratCategory; size?: number }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true }
  if (cat === 'Car')
    return (
      <svg {...p}>
        <path d="M3 13.5l1.7-4.4A2 2 0 0 1 6.6 7.8h10.8a2 2 0 0 1 1.9 1.3L21 13.5" />
        <path d="M3 13.5h18V18H3z" />
        <circle cx="7" cy="18" r="1.7" />
        <circle cx="17" cy="18" r="1.7" />
      </svg>
    )
  if (cat === 'House')
    return (
      <svg {...p}>
        <path d="M4 11l8-6 8 6" />
        <path d="M6 10v9h12v-9" />
        <path d="M10 19v-5h4v5" />
      </svg>
    )
  // Marriage → interlocking rings
  return (
    <svg {...p}>
      <circle cx="9" cy="14.5" r="4.3" />
      <circle cx="15" cy="14.5" r="4.3" />
      <path d="M9.2 5l1.3 2.6M14.8 5l-1.3 2.6M9.2 5h5.6" />
    </svg>
  )
}

/** Split "24 August 2026" → { dayNum: "24", month: "August 2026" }. */
function splitMuhuratDate(date: string) {
  const parts = (date || '').trim().split(/\s+/)
  if (parts.length >= 3) return { dayNum: parts[0], month: `${parts[1]} ${parts[2]}` }
  return { dayNum: date, month: 'Upcoming' }
}

/* The five-month season these muhurats cover. Used to spot the months a
   category has nothing in — during Chaturmas, House and Marriage stay empty
   until November. */
const MUHURAT_SEASON = ['August 2026', 'September 2026', 'October 2026', 'November 2026', 'December 2026']
const CHATURMAS_NOTE =
  'Due to Chaturmas, there are no auspicious Muhurats available this year until November for housewarming ceremonies and marriages.'

function MuhuratModalBody({ block, onClose }: { block: DailyBlock; onClose: () => void }) {
  const { rows } = useSheet(block.sheet ?? '', demoMuhurat)
  const [cat, setCat] = useState<MuhuratCategory>('Car')
  const [month, setMonth] = useState('')
  const meta = MUHURAT_META[cat]
  const filtered = rows.filter((r) => (r.category || '').trim().toLowerCase() === cat.toLowerCase())

  // A row with no time is a guidance note, not a bookable window. Notes with no
  // date apply to the whole category; notes with a month sit under that month.
  const windowRows = filtered.filter((r) => (r.time || '').trim())
  const noteRows = filtered.filter((r) => !(r.time || '').trim() && (r.note || '').trim())
  const catNotes = noteRows.filter((r) => !(r.date || '').trim()).map((r) => r.note as string)

  // Ordered, de-duped list of months that actually have windows.
  const months: string[] = []
  for (const r of windowRows) {
    const m = splitMuhuratDate(r.date || '').month
    if (!months.includes(m)) months.push(m)
  }
  // Chaturmas: any season month before the first available one is genuinely
  // empty (no windows), so surface the reason instead of a silent gap.
  const hasEarlyGap = MUHURAT_SEASON.some((m) => !months.includes(m)) && months.length > 0 && months[0] !== MUHURAT_SEASON[0]
  // Derived selection: keep the chosen month if still valid, else the first.
  const activeMonth = months.includes(month) ? month : months[0] ?? ''
  const windows = windowRows.filter((r) => splitMuhuratDate(r.date || '').month === activeMonth)
  const monthNotes = noteRows.filter((r) => splitMuhuratDate(r.date || '').month === activeMonth).map((r) => r.note as string)

  return (
    <div>
      <p className="text-[14px] leading-relaxed text-lc-mute">What are you planning? Pick one for its auspicious windows.</p>

      {/* category chips (icon + label) */}
      <div className="mt-4 flex flex-wrap gap-2">
        {muhuratCategories.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={cn(
              'flex items-center gap-2 rounded-pill px-4 py-2 text-[13px] font-semibold transition-colors duration-300',
              cat === c ? 'bg-lime text-night' : 'lc-glass-soft text-lc-ink hover:text-lc-accent',
            )}
          >
            <MuhuratIcon cat={c} />
            {c}
          </button>
        ))}
      </div>

      {/* category intent line */}
      {meta && (
        <div className="mt-4 flex items-baseline gap-2">
          <span className="label !text-[10px] text-lc-accent">{meta.ritual}</span>
          <span className="text-[13px] leading-snug text-lc-mute">· {meta.blurb}</span>
        </div>
      )}

      {/* Chaturmas: explain the empty early months for House / Marriage */}
      {hasEarlyGap && (
        <div className="mt-4 rounded-2xl border border-lc-accent/25 bg-lc-accent/10 p-3.5">
          <p className="text-[13px] leading-snug text-lc-ink">{CHATURMAS_NOTE}</p>
        </div>
      )}

      {/* category-wide guidance notes from the sheet */}
      {catNotes.length > 0 && (
        <div className="mt-3 space-y-2">
          {catNotes.map((n, i) => (
            <p key={i} className="text-[13px] leading-snug text-lc-mute">{n}</p>
          ))}
        </div>
      )}

      {/* month selector */}
      {months.length > 0 && (
        <div className="mt-5">
          <p className="label !text-[10px] text-lc-mute">Choose a month</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {months.map((m) => (
              <button
                key={m}
                onClick={() => setMonth(m)}
                className={cn(
                  'rounded-pill px-3.5 py-1.5 text-[12px] font-semibold transition-colors duration-300',
                  m === activeMonth ? 'bg-lc-accent/20 text-lc-accent ring-1 ring-lc-accent/40' : 'lc-glass-soft text-lc-mute hover:text-lc-ink',
                )}
              >
                {m.replace(' 2026', '')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* windows for the chosen month */}
      <div className="mt-5 space-y-2.5">
        {windowRows.length === 0 && !hasEarlyGap && catNotes.length === 0 && (
          <p className="text-[14px] text-lc-mute">No {cat.toLowerCase()} windows listed yet. Check back soon.</p>
        )}
        {windowRows.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="label !text-[11px] text-lc-ink">{activeMonth}</span>
            <span className="h-px flex-1 bg-white/10" />
            <span className="label !text-[10px] text-lc-mute">{windows.length} {windows.length === 1 ? 'window' : 'windows'}</span>
          </div>
        )}
        {monthNotes.map((n, i) => (
          <p key={`mn-${i}`} className="text-[13px] leading-snug text-lc-accent">{n}</p>
        ))}
        {windows.map((r, i) => {
          const { dayNum } = splitMuhuratDate(r.date || '')
          return (
            <div key={i} className="lc-glass-soft flex items-center gap-4 rounded-2xl p-4">
              <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-black/20 text-center leading-none">
                <span className="text-[18px] font-bold text-lc-ink">{dayNum}</span>
                <span className="label mt-1 !text-[9px] text-lime">{(r.day || '').slice(0, 3)}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-lc-ink">{r.time}</p>
                {r.note && <p className="mt-0.5 text-[13px] leading-snug text-lc-mute">{r.note}</p>}
              </div>
            </div>
          )
        })}
      </div>
      <JoinCta onClose={onClose} demo={!sheetConfigured} />
    </div>
  )
}

/* ---- page ---------------------------------------------------------- */
export default function HeroCosmos({ dark = true }: { dark?: boolean }) {
  useSmoothScroll()
  // Book/join CTAs deep-link into the webapp (falls back to the in-page
  // anchor). The webapp owns auth and prompts guests to sign in itself.
  const consultTarget = consultWebappUrl('Cosmos') ?? '#consult'
  // Per-crafter booking: match a curated astrologer to their webapp booking id;
  // unmatched fall back to the generic Cosmos booking.
  const lcIndex = useLifecrafterIndex()
  const crafterBook = (name: string) => consultWebappUrl('Cosmos', lookupLifecrafterId(lcIndex, name)) ?? '#consult'
  // Daily / Weekend consultations → the "Cosmos Program Hub" crafter's 1-1
  // booking (live id if the feed loaded, else the hardcoded fallback).
  const programHubTarget =
    consultWebappUrl('Cosmos', lookupLifecrafterId(lcIndex, COSMOS_PROGRAM_HUB_NAME) ?? COSMOS_PROGRAM_HUB_ID) ?? '#consult'
  // Join → the Cosmos webapp home (the webapp handles guest vs member).
  const joinTarget = communityHomeUrl('Cosmos') ?? '#join'
  const circlesTarget = communityWebappUrl('Cosmos', 'community_circles') ?? '#circles'
  const eventsTarget = communityWebappUrl('Cosmos', 'events') ?? '/communities'
  // This month's live Cosmos events (null while loading, [] when none).
  const cosmosEvents = useCommunityEvents('Cosmos')
  const [dailyOpen, setDailyOpen] = useState<string | null>(null)
  const [openGenre, setOpenGenre] = useState<string | null>('Vedic')
  const [profileMentor, setProfileMentor] = useState<(typeof cosmosMentors)[number] | null>(null)
  // Mentors are priced per person → "Price on request" opens an enquiry form.
  const [requestFor, setRequestFor] = useState<string | null>(null)
  const [mentorLang, setMentorLang] = useState<'en' | 'hi'>('en')
  // Horizontal mentor rail: arrows at each end make it obvious the row scrolls
  // (and that mentors continue off-screen). We fade an arrow out at its extreme.
  const mentorRailRef = useRef<HTMLDivElement>(null)
  const [railEnds, setRailEnds] = useState({ atStart: true, atEnd: false })
  const updateRailEnds = () => {
    const el = mentorRailRef.current
    if (!el) return
    const overflows = el.scrollWidth - el.clientWidth > 4
    setRailEnds({
      atStart: el.scrollLeft <= 4,
      atEnd: !overflows || el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
    })
  }
  const scrollRail = (dir: -1 | 1) => {
    const el = mentorRailRef.current
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 640), behavior: 'smooth' })
  }
  useEffect(() => {
    updateRailEnds()
    window.addEventListener('resize', updateRailEnds)
    return () => window.removeEventListener('resize', updateRailEnds)
  }, [])
  // Same treatment for the sub-genres ("ways to read the sky") scroller.
  const pathRailRef = useRef<HTMLDivElement>(null)
  const [pathEnds, setPathEnds] = useState({ atStart: true, atEnd: false })
  const updatePathEnds = () => {
    const el = pathRailRef.current
    if (!el) return
    const overflows = el.scrollWidth - el.clientWidth > 4
    setPathEnds({
      atStart: el.scrollLeft <= 4,
      atEnd: !overflows || el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
    })
  }
  const scrollPathRail = (dir: -1 | 1) => {
    const el = pathRailRef.current
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 640), behavior: 'smooth' })
  }
  useEffect(() => {
    updatePathEnds()
    window.addEventListener('resize', updatePathEnds)
    return () => window.removeEventListener('resize', updatePathEnds)
  }, [])
  // Book-a-consultation bio pop-up (Premium / Elite astrologers).
  const [craftBio, setCraftBio] = useState<ConsultCrafter | null>(null)
  const [craftLang, setCraftLang] = useState<'en' | 'hi'>('en')
  // "View all" per-tier roster pop-up.
  const [laneTier, setLaneTier] = useState<string | null>(null)
  // Live circles from the webapp only — no static stand-ins. Icon + artwork
  // reuse a curated entry at the same index so the round mask never renders
  // blank. No live circles → the section shows a "create your circle" CTA.
  const liveCircles = useCommunityCircles('Cosmos')
  const circles = (liveCircles ?? []).map((c, i) => {
    const base = cosmosCircles[i % cosmosCircles.length]
    return { name: c.name, short: c.name, blurb: c.blurb, icon: base.icon, image: c.image || base.image, members: '' }
  })

  return (
    <div className={cn('relative min-h-screen overflow-x-clip text-lc-ink', dark ? 'lc-dark bg-night-800' : 'lc-light bg-cream-50')}>
      <Backdrop dark={dark} />
      <SiteNav />

      <main className="relative z-10">
        {/* hero: copy left, intro video right */}
        <section id="top" className="relative px-5 pb-14 pt-36 md:pt-44">
          <HeroSparkles />
          <div className="container-lc relative grid items-start gap-12 lg:grid-cols-12">
            <div className="order-1 lg:col-span-6">
              <p className={cn('font-sans text-[clamp(0.75rem,0.55rem+0.7vw,0.95rem)] font-bold uppercase tracking-[0.3em]', muteText(dark))}>
                Community · Astrology
              </p>
              <h1 className={cn('mt-5 font-display text-[clamp(2.6rem,1.4rem+4.4vw,6rem)] font-normal leading-[0.95] tracking-[0.01em]', inkText(dark))}>
                Cosmos
              </h1>
              <p className={cn('mt-2 font-display text-[clamp(1.35rem,0.9rem+1.8vw,2.8rem)] font-normal leading-[1.05] tracking-[0.01em]', inkText(dark))}>
                written in the{' '}
                <span className="inline-block -rotate-1 rounded-[0.28em] bg-lime px-[0.2em] pb-[0.05em] text-[#141D12]">stars</span>.
              </p>
              <p className={cn('mt-5 max-w-xl text-[15px] leading-relaxed sm:mt-6 xl:text-[17px]', muteText(dark))}>{cosmos.intro}</p>
              <div className="mt-7 flex flex-wrap items-center gap-3 sm:mt-8 sm:gap-4">
                <MagneticButton href={consultTarget} data-cursor="join" className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night xl:px-7 xl:py-3.5 xl:text-[16px]">
                  Book a consultation
                </MagneticButton>
                <MagneticButton href={joinTarget} className={cn('rounded-pill px-6 py-3 text-[15px] font-bold xl:px-7 xl:py-3.5 xl:text-[16px]', glass(dark), inkText(dark))}>
                  Join the community
                </MagneticButton>
                <a href="#events" className={cn('label group inline-flex items-center gap-2 transition-colors', muteText(dark))}>
                  Join an event <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
              </div>
              <ParticipationNote className="mt-5" />
            </div>

            <div className="order-2 lg:col-span-6">
              <VideoPanel
                layout="reels"
                label="Cosmos in 30 seconds"
                videos={[
                  { src: '/video/cosmos-gurumaa.mp4', poster: '/img/cosmos-gurumaa.jpg' },
                  { src: '/video/cosmos-hsrawat.mp4', poster: '/img/cosmos-hsrawat.jpg' },
                  { src: '/video/cosmos-manju.mp4', poster: '/img/cosmos-manju.jpg' },
                  { src: '/video/cosmos-shweta.mp4', poster: '/img/cosmos-shweta.jpg' },
                ]}
              />
            </div>
          </div>
        </section>

        {/* daily engagement layer: tap a block, get a taste in a modal */}
        <section id="daily" className="relative px-5 py-[9vh]">
          <div className="container-lc">
            <p className={cn('label text-center', muteText(dark))}>Every day in Cosmos</p>
            <h2 className={cn('mx-auto mt-4 max-w-[18ch] text-center font-display text-[clamp(1.6rem,1.05rem+2.2vw,3.4rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
              The sky, served daily.
            </h2>
            <p className={cn('mx-auto mt-3 max-w-lg text-center text-lg', muteText(dark))}>
              Tap a block for a taste. The full versions live inside the community and change with the sky.
            </p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cosmosDaily.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setDailyOpen(b.key)}
                  className={cn('group rounded-[24px] p-6 text-left transition-transform duration-300 ease-brand hover:-translate-y-1', glass(dark))}
                >
                  <div className="flex items-start justify-between">
                    <span className={cn('grid h-11 w-11 place-items-center rounded-xl transition-transform duration-500 group-hover:rotate-12', glassSoft(dark), 'text-lc-accent')}>
                      <Icon name={b.icon} size={20} />
                    </span>
                    <span className={cn('label !text-[11px] opacity-0 transition-opacity duration-300 group-hover:opacity-100', muteText(dark))}>open →</span>
                  </div>
                  <h3 className={cn('mt-5 font-display text-xl font-normal tracking-[0.01em]', inkText(dark))}>{b.title}</h3>
                  <p className={cn('mt-1.5 text-[14px] leading-relaxed', muteText(dark))}>{b.teaser}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* the daily-block modal */}
        {(() => {
          const b = cosmosDaily.find((d) => d.key === dailyOpen)
          const close = () => setDailyOpen(null)
          const big = b?.kind != null
          return (
            <Modal open={!!b} onClose={close} title={b?.title ?? ''} size={big ? 'lg' : 'md'}>
              {b &&
                (b.kind === 'muhurat' ? (
                  <MuhuratModalBody block={b} onClose={close} />
                ) : b.kind ? (
                  <ZodiacModalBody block={b} onClose={close} />
                ) : (
                  <>
                    <div className="space-y-3">
                      {b.body.map((line) => (
                        <p key={line} className="text-[15px] leading-relaxed text-lc-mute">{line}</p>
                      ))}
                    </div>
                    <JoinCta onClose={close} demo />
                  </>
                ))}
            </Modal>
          )
        })()}

        {/* LifeCrafters lead: big consultation lanes, then compact mentors */}
        <section className="relative px-5 py-[8vh]">
          <div className="container-lc">
            <p className={cn('label text-center', muteText(dark))}>Your LifeCrafters</p>
            <h2 className={cn('mx-auto mt-4 text-center font-display text-[clamp(1.6rem,1.05rem+2.2vw,3.4rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
              Book a consultation.
            </h2>
            <p className={cn('mx-auto mt-3 max-w-xl text-center text-lg', muteText(dark))}>
              Verified LifeCrafters, ready when you are. Tap a name for their bio, or book straight away.
            </p>

            {/* LifeCrafters: Premium & Elite lanes. Tap a name for the bio (EN / हिं). */}
            <div className="mt-10">
              <div className="grid gap-5 md:grid-cols-2">
                {cosmosConsultTiers.map((lane) => {
                  const laneCrafters = cosmosConsultCrafters.filter((c) => c.tier === lane.tier)
                  return (
                  <div key={lane.tier} className={cn('rounded-[22px] p-6', glassSoft(dark))}>
                    <div className="flex items-baseline justify-between gap-3">
                      <p className={cn('font-display text-2xl font-normal tracking-[0.01em]', inkText(dark))}>{lane.tier}</p>
                      <span className="label shrink-0 !text-[11px] text-lc-accent">
                        <span className={cn('mr-1 font-normal line-through', muteText(dark))}>{lane.original}</span>
                        {lane.price} · {lane.note}
                      </span>
                    </div>
                    <div className="mt-5 space-y-4">
                      {laneCrafters.slice(0, 3).map((p) => (
                        <div key={p.name} className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => { setCraftLang('en'); setCraftBio(p) }}
                            className="group relative block h-16 w-16 shrink-0 overflow-hidden rounded-full ring-2 ring-white/20"
                            aria-label={`About ${p.name}`}
                          >
                            {p.photo ? (
                              <img src={p.photo} alt={p.name} style={{ objectPosition: p.imgPos }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110 motion-reduce:group-hover:scale-100" />
                            ) : (
                              <span className="grid h-full w-full place-items-center bg-white/10 text-lc-accent">
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6Z" /></svg>
                              </span>
                            )}
                          </button>
                          <div className="min-w-0 flex-1">
                            <button
                              onClick={() => { setCraftLang('en'); setCraftBio(p) }}
                              className={cn('block max-w-full truncate text-left text-[15px] font-semibold underline-offset-2 hover:underline', inkText(dark))}
                            >
                              {p.name}
                            </button>
                            <p className={cn('mt-0.5 truncate text-[12px]', muteText(dark))}>{p.focus}</p>
                          </div>
                          <a href={crafterBook(p.name)} className="shrink-0 rounded-pill bg-lime px-4 py-2 text-[13px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                            Book
                          </a>
                        </div>
                      ))}
                    </div>
                    {laneCrafters.length > 3 && (
                      <button
                        type="button"
                        onClick={() => setLaneTier(lane.tier)}
                        className={cn('mt-5 w-full rounded-pill px-4 py-2.5 text-[13px] font-bold transition-transform duration-300 ease-brand hover:scale-[1.01] motion-reduce:hover:scale-100', glass(dark), inkText(dark))}
                      >
                        View all {laneCrafters.length}
                      </button>
                    )}
                  </div>
                  )
                })}
              </div>
            </div>

            {/* consultation windows */}
            <div id="consult" className="mt-8 grid gap-4 md:grid-cols-2">
              {[cosmosConsult.daily, cosmosConsult.weekend].map((c) => (
                <div key={c.title} className={cn('flex flex-col gap-5 rounded-[24px] p-6 sm:flex-row sm:items-center sm:justify-between', glassSoft(dark))}>
                  <div>
                    <p className={cn('font-display text-xl font-normal tracking-[0.01em]', inkText(dark))}>{c.title}</p>
                    <p className={cn('label mt-1 !text-[11px] text-lc-accent')}>{c.when}</p>
                    <p className={cn('mt-2 max-w-sm text-[13px] leading-relaxed', muteText(dark))}>{c.note}</p>
                  </div>
                  <div className="flex shrink-0 items-center justify-between gap-4 sm:flex-col sm:items-end sm:gap-3">
                    <p className="leading-none">
                      <span className={cn('mr-1.5 align-middle text-lg font-normal line-through', muteText(dark))}>{c.original}</span>
                      <span className={cn('font-display text-3xl font-normal tracking-[0.01em]', inkText(dark))}>{c.price}</span>
                    </p>
                    <a href={programHubTarget} className="shrink-0 rounded-pill bg-lime px-5 py-2.5 text-[14px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02]">
                      Book consultation
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Vaastu consultation — priced on request (new house, factory, commercial) */}
            <div className={cn('mt-4 flex flex-col gap-5 rounded-[24px] p-6 sm:flex-row sm:items-center sm:justify-between', glassSoft(dark))}>
              <div>
                <p className={cn('font-display text-xl font-normal tracking-[0.01em]', inkText(dark))}>Vaastu Consultation</p>
                <p className={cn('mt-2 max-w-sm text-[13px] leading-relaxed', muteText(dark))}>New house, factory or commercial property.</p>
              </div>
              <button onClick={() => setRequestFor('Vaastu Consultation')} className="shrink-0 rounded-pill bg-lime px-5 py-2.5 text-[14px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02]">
                Request for price
              </button>
            </div>

            {/* mentors: deeper, personal readings — compact cards below the LifeCrafters */}
            <div className="mt-14">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <p className={cn('label', muteText(dark))}>Mentors · guides for every path</p>
                <span className="label !text-[11px] text-lc-accent">Personal readings · price on request</span>
              </div>
              {(() => {
                {/* few mentors → a full-width grid of bigger cards with their blurbs;
                    many → a compact horizontal rail with scroll arrows at each end */}
                const few = cosmosMentors.length <= 4
                const pos: Record<string, string> = {
                  'Dr. Arun Bansal': 'center 60%',
                  'Nukala Suryanarayana': 'center 28%',
                  'Vivek Kush': 'center 28%',
                  'H.S. Rawat': 'center 28%',
                  'Acharya Poonam Gaur': 'center 38%',
                  'Naresh Kumar Sharma': 'center 20%',
                }
                const cards = cosmosMentors.map((m) => (
                  <figure key={m.name} className={cn('group flex flex-col overflow-hidden rounded-[22px] !shadow-none transition-transform duration-300 hover:-translate-y-1', glass(dark), !few && 'w-[280px] shrink-0 snap-start')}>
                    <div className="relative aspect-square overflow-hidden">
                      <img src={m.photo} alt={m.name} style={{ objectPosition: pos[m.name] ?? 'center top' }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:group-hover:scale-100" />
                    </div>
                    <figcaption className="flex flex-1 flex-col p-5">
                      <p className={cn('font-display text-xl font-normal tracking-[0.01em]', inkText(dark))}>{m.displayName ?? m.name}</p>
                      <p className={cn('label mt-0.5 !text-[11px]', muteText(dark))}>{m.focus}</p>
                      {m.short && <p className={cn('mt-2.5 line-clamp-2 text-[14px] leading-relaxed', muteText(dark))}>{m.short}</p>}
                      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4">
                        {MENTOR_BOOK_NAMES.has(m.name) ? (
                          <a href={crafterBook(m.name)} className="rounded-pill bg-lime px-3 py-1.5 text-[12px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                            Book consultation
                          </a>
                        ) : (
                          <button onClick={() => setRequestFor(m.name)} className="rounded-pill bg-lime px-3 py-1.5 text-[12px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                            Price on request
                          </button>
                        )}
                        {m.about && (
                          <button onClick={() => { setMentorLang('en'); setProfileMentor(m) }} className="label !text-[11px] text-lc-accent underline-offset-2 hover:underline">
                            View profile
                          </button>
                        )}
                      </div>
                    </figcaption>
                  </figure>
                ))
                if (few) {
                  return (
                    <div className={cn('mt-5 grid gap-4 sm:grid-cols-2', cosmosMentors.length >= 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')}>
                      {cards}
                    </div>
                  )
                }
                // Arrows only on sm+ ; on mobile the swipe/scroll is already obvious.
                const arrowCls = cn('absolute top-[140px] z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition duration-300 sm:grid', glass(dark), inkText(dark))
                return (
                <div className="relative left-1/2 mt-5 w-screen -translate-x-1/2">
                  <div
                    ref={mentorRailRef}
                    onScroll={updateRailEnds}
                    className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-2"
                    style={{ paddingLeft: RAIL_EDGE, paddingRight: RAIL_EDGE, scrollPaddingLeft: RAIL_EDGE, scrollPaddingRight: RAIL_EDGE }}
                  >
                    {cards}
                  </div>
                  <button
                    type="button"
                    aria-label="Show previous mentors"
                    onClick={() => scrollRail(-1)}
                    disabled={railEnds.atStart}
                    className={cn(arrowCls, 'left-3 sm:left-5', railEnds.atStart ? 'pointer-events-none opacity-0' : 'opacity-100 hover:scale-105')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                  </button>
                  <button
                    type="button"
                    aria-label="Show more mentors"
                    onClick={() => scrollRail(1)}
                    disabled={railEnds.atEnd}
                    className={cn(arrowCls, 'right-3 sm:right-5', railEnds.atEnd ? 'pointer-events-none opacity-0' : 'opacity-100 hover:scale-105')}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                  </button>
                </div>
                )
              })()}
              <p className={cn('label mt-5 text-center !text-[11px]', muteText(dark))}>
                Pricing on request: our team confirms each mentor's available slots and rates before you book.
              </p>
            </div>
          </div>
        </section>


        {/* paths */}
        <section className="relative px-5 py-[8vh]">
          <div className="container-lc">
            <p className={cn('label text-center', muteText(dark))}>Sub-genres</p>
            <h2 className={cn('mx-auto mt-4 max-w-[16ch] text-center font-display text-[clamp(1.6rem,1.05rem+2.2vw,3.4rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
              Seven ways to read the sky.
            </h2>
          </div>

          {/* full-bleed scroller: cards run to the viewport edge, first card
              aligns with the page content. Arrows (sm+) make the scroll obvious. */}
          <div className="relative mt-10">
          <div
            ref={pathRailRef}
            onScroll={updatePathEnds}
            className="no-scrollbar flex snap-x gap-4 overflow-x-auto pt-2 pb-3"
            style={{
              paddingLeft: 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))',
              paddingRight: 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))',
              scrollPaddingLeft: 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))',
              scrollPaddingRight: 'calc(max((100vw - 1360px) / 2, 0px) + clamp(1.25rem, 0.5rem + 3vw, 3.5rem))',
            }}
          >
              {cosmos.paths.map((p) => {
                const active = openGenre === p.name
                return (
                  <button
                    key={p.name}
                    onClick={() => setOpenGenre(p.name)}
                    aria-expanded={active}
                    className={cn(
                      'lc-menu group w-[250px] shrink-0 snap-start rounded-[24px] p-5 text-left transition-all duration-300 ease-brand hover:-translate-y-1 !shadow-none',
                      active && 'ring-2 ring-lime/70',
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <span className={cn('grid h-11 w-11 place-items-center rounded-xl transition-transform duration-500 group-hover:rotate-12', glassSoft(dark), 'text-lc-accent')}>
                        <Icon name={p.icon} size={20} />
                      </span>
                      <span
                        className={cn('grid h-8 w-8 place-items-center rounded-full transition-colors duration-300', glassSoft(dark), muteText(dark), active && '!bg-lime !text-night')}
                        aria-hidden="true"
                      >
                        {active ? (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>
                        ) : (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                        )}
                      </span>
                    </div>
                    <h3 className={cn('mt-5 font-display text-xl font-normal tracking-[0.01em]', inkText(dark))}>{p.name}</h3>
                    <p className={cn('mt-2 text-[14px] leading-relaxed', muteText(dark))}>{p.body}</p>
                    <p className={cn('label mt-4 !text-[11px] transition-colors', active ? 'text-lc-accent' : muteText(dark))}>
                      {active ? 'Reading below' : 'Tap to learn more'}
                    </p>
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              aria-label="Show previous sub-genres"
              onClick={() => scrollPathRail(-1)}
              disabled={pathEnds.atStart}
              className={cn('absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition duration-300 sm:grid', 'left-3 sm:left-5', glass(dark), inkText(dark), pathEnds.atStart ? 'pointer-events-none opacity-0' : 'opacity-100 hover:scale-105')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
            </button>
            <button
              type="button"
              aria-label="Show more sub-genres"
              onClick={() => scrollPathRail(1)}
              disabled={pathEnds.atEnd}
              className={cn('absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition duration-300 sm:grid', 'right-3 sm:right-5', glass(dark), inkText(dark), pathEnds.atEnd ? 'pointer-events-none opacity-0' : 'opacity-100 hover:scale-105')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
            </button>
          </div>

          <div className="container-lc">
            {/* the deep dive: opens under the grid */}
            <div className={cn('grid transition-[grid-template-rows] duration-500 ease-brand', openGenre ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
              <div className="overflow-hidden">
                {openGenre && cosmosSubgenres[openGenre] && (() => {
                  const d = cosmosSubgenres[openGenre]
                  const pathImage = cosmos.paths.find((p) => p.name === openGenre)?.image
                  return (
                    <div className={cn('mt-6 overflow-hidden rounded-[28px] !shadow-none', glass(dark))}>
                      <div className="p-5 xl:p-7 md:p-10">
                      <div className="flex flex-col gap-6 sm:flex-row sm:items-stretch">
                        {pathImage && (
                          <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-2xl sm:aspect-auto sm:w-52 md:w-60">
                            <img src={pathImage} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline justify-between gap-3">
                            <div>
                              <h3 className={cn('font-display text-3xl font-normal tracking-[0.01em]', inkText(dark))}>{openGenre} astrology</h3>
                              <p className="label mt-1 !text-[11px] text-lc-accent">{d.tagline}</p>
                            </div>
                          </div>
                          <p className={cn('label mt-6 !text-[11px]', inkText(dark))}>What is it?</p>
                          <p className={cn('mt-3 max-w-2xl text-[15px] xl:text-[16px] leading-relaxed', muteText(dark))}>{d.what}</p>
                        </div>
                      </div>

                      <div className="mt-8 grid gap-8 md:grid-cols-3">
                        <div>
                          <p className={cn('label !text-[11px]', inkText(dark))}>When should I consult?</p>
                          <ul className="mt-4 space-y-2.5">
                            {d.consult.map((cq) => (
                              <li key={cq} className={cn('flex items-start gap-2.5 text-[14px] leading-snug', muteText(dark))}>
                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" />
                                {cq}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className={cn('label !text-[11px]', inkText(dark))}>What you'll gain</p>
                          <ul className="mt-4 space-y-2.5">
                            {d.gain.map((g) => (
                              <li key={g} className={cn('flex items-start gap-2.5 text-[14px] leading-snug', muteText(dark))}>
                                <span className={cn('mt-0.5 shrink-0', 'text-lc-accent')} aria-hidden="true">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m4.5 12.5 5 5 10-11" /></svg>
                                </span>
                                {g}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className={cn('rounded-[20px] p-5', glassSoft(dark))}>
                          <p className={cn('label !text-[11px]', inkText(dark))}>Explore {openGenre} in Cosmos</p>
                          <div className="mt-4 flex flex-col gap-2.5">
                            <a href={consultTarget}className="rounded-pill bg-lime px-4 py-2.5 text-center text-[14px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">Book a consultation</a>
                            <a href={circlesTarget} className={cn('rounded-pill px-4 py-2.5 text-center text-[14px] font-bold transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100', glass(dark), inkText(dark))}>Join a circle</a>
                          </div>
                        </div>
                      </div>

                      <p className={cn('label mt-8 border-t pt-5 !text-[11px]', dark ? 'border-white/10' : 'border-ink/10', muteText(dark))}>
                        Good to know · <span className={muteText(dark)}>{d.note}</span>
                      </p>
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>
        </section>

        {/* cosmos events (above the circles + partner pitch) */}
        <section id="events" className="relative px-5 py-[8vh]">
          <div className="container-lc">
            <p className={cn('label text-center', muteText(dark))}>What’s on</p>
            <h2 className={cn('mx-auto mt-4 max-w-[34ch] text-center font-display text-[clamp(1.7rem,1.1rem+2.2vw,2.8rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
              {cosmosEvents && cosmosEvents.length > 0
                ? 'Sessions worth showing up for.'
                : placeholderEventsFor('Cosmos').length > 0
                  ? 'Experiences worth the trip.'
                  : 'Events are coming soon.'}
            </h2>
            {cosmosEvents && cosmosEvents.length > 0 ? (
              <>
                <div className="mx-auto mt-10 max-w-3xl space-y-3">
                  {cosmosEvents.map((e, i) => (
                    <EventRow key={e.id + ':' + i} day={e.day} time={e.time} kicker={e.kicker} title={e.title} blurb={e.blurb} href={eventWebappUrl(e.id)} />
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <a href={eventsTarget} className={cn('inline-flex items-center gap-2 rounded-pill px-6 py-3 text-[15px] font-bold transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100', glass(dark), inkText(dark))}>
                    View all events
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                  </a>
                </div>
              </>
            ) : placeholderEventsFor('Cosmos').length > 0 ? (
              <PlaceholderEvents community="Cosmos" theme="dark" className="mt-2" />
            ) : (
              <>
                <p className={cn('mx-auto mt-4 max-w-xl text-center text-lg', muteText(dark))}>
                  We’re still finalising the Cosmos calendar. Curated live sessions, workshops and gatherings will land here shortly. Check back soon.
                </p>
                <div className="mt-10 flex justify-center">
                  <span className={cn('label rounded-pill px-5 py-2.5 !text-[12px]', glassSoft(dark), muteText(dark))}>
                    Calendar in the works · coming soon
                  </span>
                </div>
              </>
            )}
          </div>
        </section>

        {/* circles: interactive circular sub-communities within Cosmos */}
        <section id="circles" className="relative px-5 py-[8vh]">
          <div className="container-lc">
            <p className={cn('label text-center', muteText(dark))}>Find your people</p>
            <h2 className={cn('mx-auto mt-4 max-w-[16ch] text-center font-display text-[clamp(1.6rem,1.05rem+2.2vw,3.4rem)] font-normal leading-[1.02] tracking-[0.01em]', inkText(dark))}>
              Join Your Circle.
            </h2>
            <p className={cn('mx-auto mt-3 max-w-xl text-center text-lg', muteText(dark))}>
              Cosmos is bigger than one feed. Step into a smaller circle: a room of people practising the same craft, meeting live and talking daily. Every circle is free.
            </p>

            {liveCircles && circles.length === 0 ? (
              <div className={cn('mx-auto mt-12 max-w-xl rounded-[28px] p-10 text-center', glassSoft(dark))}>
                <p className={cn('font-display text-2xl font-normal tracking-[0.01em]', inkText(dark))}>No circles here yet.</p>
                <p className={cn('mx-auto mt-3 max-w-md text-[15px] leading-relaxed', muteText(dark))}>
                  Be the first. Start a circle and gather the people who practise what you love.
                </p>
                <a
                  href={circlesTarget}
                  data-cursor="join"
                  className="mt-7 inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-7 xl:py-3.5 font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
                >
                  Create your circle
                </a>
              </div>
            ) : (
            <div
              className={cn(
                'mt-12',
                circles.length > 4
                  ? 'no-scrollbar flex snap-x gap-6 overflow-x-auto px-1 pb-3 sm:gap-10'
                  : 'flex flex-wrap justify-center gap-x-6 gap-y-10 sm:gap-x-10',
              )}
            >
              {circles.map((c, i) => (
                <a
                  key={c.name}
                  href={circlesTarget}
                  data-cursor="join"
                  className="group flex w-[42vw] max-w-[188px] shrink-0 snap-start flex-col items-center text-center outline-none sm:w-[188px]"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  {/* circular masked artwork with orbiting ring */}
                  <span className="relative block aspect-square w-full max-w-[188px]">
                    {/* rotating dashed orbit ring */}
                    <span
                      className="pointer-events-none absolute -inset-2 rounded-full border border-dashed border-lc-line opacity-40 transition-opacity duration-500 group-hover:opacity-90 motion-safe:group-hover:[animation:spin_14s_linear_infinite]"
                      aria-hidden="true"
                    />
                    {/* accent glow on hover */}
                    <span
                      className="pointer-events-none absolute -inset-1 rounded-full opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-70"
                      style={{ background: 'radial-gradient(closest-side, rgba(124,58,237,.55), transparent)' }}
                      aria-hidden="true"
                    />
                    <span className="relative block h-full w-full overflow-hidden rounded-full ring-1 ring-lc-line transition-transform duration-500 ease-brand group-hover:-translate-y-1.5 group-hover:scale-[1.03] group-hover:ring-lime/70">
                      <img src={c.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                      {/* icon chip, revealed on hover */}
                      <span className="absolute inset-0 grid place-items-end justify-items-center pb-3">
                        <span className="inline-flex items-center gap-1.5 rounded-pill bg-night/70 px-3 py-1.5 text-[11px] font-semibold text-lc-fog opacity-0 backdrop-blur-sm transition-all duration-500 group-hover:opacity-100 group-hover:-translate-y-0.5">
                          <Icon name={c.icon} size={13} /> Join circle
                        </span>
                      </span>
                    </span>
                  </span>

                  <h3 className={cn('mt-6 font-display text-xl font-normal tracking-[0.01em]', inkText(dark))}>{c.short}</h3>
                  <p className={cn('mt-1.5 max-w-[24ch] text-[13px] leading-relaxed', muteText(dark))}>{c.blurb}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5">
                    <span className="label rounded-pill bg-lime/90 px-2 py-0.5 !text-[10px] text-night">Free</span>
                    {c.members && <span className={cn('label !text-[10px]', muteText(dark))}>{c.members} members</span>}
                  </span>
                </a>
              ))}
            </div>
            )}

            <p className={cn('label mt-12 text-center !text-[11px]', muteText(dark))}>
              All circles are free to join · new circles open as the community grows
            </p>
          </div>
        </section>

        {/* partner pitch: the marketplace grid returns once partners are aboard */}
        <PartnerCallout dark accent="#7C3AED" categories={cosmosMarketplace.categories} />

        {/* values */}
        <section className="relative px-5 py-[8vh]">
          <div className="container-lc grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cosmos.values.map((v) => (
              <div key={v.q} className={cn('rounded-[22px] p-5', glassSoft(dark))}>
                <h3 className={cn('font-display text-lg font-normal tracking-[0.01em]', inkText(dark))}>{v.q}</h3>
                <p className={cn('mt-1.5 text-[13px] leading-relaxed', muteText(dark))}>{v.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* closing */}
        <section id="join" className="relative px-5 py-[10vh]">
          <div className="container-lc">
            <div className={cn('relative overflow-hidden rounded-[36px] px-6 py-16 text-center md:py-20', glass(dark))}>
              <span className="blob absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 opacity-40" style={{ background: '#7C3AED' }} aria-hidden="true" />
              <div className="relative">
                <div className="font-display text-4xl text-lime">✦</div>
                <h2 className={cn('mx-auto mt-4 max-w-[18ch] font-display text-[clamp(2.2rem,1.2rem+4.6vw,4.6rem)] font-normal leading-[0.98] tracking-[0.01em]', inkText(dark))}>
                  Your sky is waiting. Come read it with us.
                </h2>
                <MagneticButton href={joinTarget} data-cursor="join" className="mt-8 rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-9 xl:py-4 xl:text-[18px] font-bold text-night">
                  Join the community
                </MagneticButton>
                <p className={cn('label mt-5', muteText(dark))}>A daily practice, live circles, real astrologers.</p>
              </div>
            </div>
          </div>
        </section>

        {/* mentor full profile */}
        <Modal open={!!profileMentor} onClose={() => setProfileMentor(null)} title={(mentorLang === 'hi' && profileMentor?.aboutHi?.length ? profileMentor.nameHi ?? profileMentor.name : profileMentor?.name) ?? ''} size="lg">
          {profileMentor && (
            <div>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <img src={profileMentor.photo} alt={profileMentor.name} className="h-24 w-24 shrink-0 rounded-2xl object-cover ring-2 ring-white/50" />
                <div className="min-w-0">
                  {(() => {
                    const hi = mentorLang === 'hi' && !!profileMentor.aboutHi?.length
                    return (
                      <>
                        <p lang={hi ? 'hi' : 'en'} className="label mt-0.5 text-lc-mute">{hi ? profileMentor.focusHi ?? profileMentor.focus : profileMentor.focus}</p>
                        {profileMentor.years && (
                          <p lang={hi ? 'hi' : 'en'} className="mt-1 text-[12px] text-lc-mute">
                            {hi ? `${profileMentor.yearsHi ?? profileMentor.years} अनुभव` : `${profileMentor.years} experience`}
                          </p>
                        )}
                      </>
                    )
                  })()}
                </div>
              </div>

              {(() => {
                const hasHi = !!profileMentor.aboutHi?.length
                const hi = mentorLang === 'hi' && hasHi
                const about = hi ? profileMentor.aboutHi! : profileMentor.about
                const expertise = hi && profileMentor.expertiseHi?.length ? profileMentor.expertiseHi : profileMentor.expertise
                return (
                  <>
                    {about && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between gap-3">
                          <p className="label !text-[11px] text-lc-ink">{hi ? 'परिचय' : 'About'}</p>
                          {hasHi && (
                            <div className="lc-glass-soft inline-flex shrink-0 rounded-pill p-0.5 text-[12px] font-bold">
                              {(['en', 'hi'] as const).map((l) => (
                                <button
                                  key={l}
                                  onClick={() => setMentorLang(l)}
                                  className={cn('rounded-pill px-3 py-1 transition-colors', mentorLang === l ? 'bg-lime text-night' : 'text-lc-mute hover:text-lc-ink')}
                                >
                                  {l === 'en' ? 'EN' : 'हिं'}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div lang={hi ? 'hi' : 'en'} className="mt-2 space-y-2.5 text-[14px] leading-relaxed text-lc-mute">
                          {about.map((para, i) => <p key={i}>{para}</p>)}
                        </div>
                      </div>
                    )}

                    {expertise && (
                      <div className="mt-6">
                        <p className="label !text-[11px] text-lc-ink">{hi ? 'विशेषज्ञता' : 'Expertise'}</p>
                        <div lang={hi ? 'hi' : 'en'} className="mt-3 flex flex-wrap gap-1.5">
                          {expertise.map((e) => (
                            <span key={e} className="lc-glass-soft rounded-pill px-2.5 py-1 text-[12px] text-lc-ink">{e}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )
              })()}

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a href={consultTarget} onClick={() => setProfileMentor(null)} className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">Book a session</a>
                <span className="label !text-[11px] text-lc-mute">Pricing on request: our team confirms slots and rates before you book.</span>
              </div>
            </div>
          )}
        </Modal>

        {/* book-a-consultation astrologer bio (Premium / Elite) */}
        <Modal open={!!craftBio} onClose={() => setCraftBio(null)} title={craftBio?.name ?? ''} size="md">
          {craftBio && (() => {
            const hasHi = !!craftBio.aboutHi?.length
            const hi = craftLang === 'hi' && hasHi
            const about = hi ? craftBio.aboutHi! : craftBio.about
            return (
              <div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <span className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-2xl bg-white/10 text-lc-accent ring-2 ring-white/40">
                    {craftBio.photo ? (
                      <img src={craftBio.photo} alt={craftBio.name} className="h-full w-full object-cover" />
                    ) : (
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6Z" /></svg>
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="label mt-0.5 text-lc-mute">{craftBio.focus}</p>
                    {craftBio.years && <p className="mt-1 text-[12px] text-lc-mute">{craftBio.years} experience</p>}
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between gap-3">
                    <p className="label !text-[11px] text-lc-ink">{hi ? 'परिचय' : 'About'}</p>
                    {hasHi && (
                      <div className="lc-glass-soft inline-flex shrink-0 rounded-pill p-0.5 text-[12px] font-bold">
                        {(['en', 'hi'] as const).map((l) => (
                          <button
                            key={l}
                            onClick={() => setCraftLang(l)}
                            className={cn('rounded-pill px-3 py-1 transition-colors', craftLang === l ? 'bg-lime text-night' : 'text-lc-mute hover:text-lc-ink')}
                          >
                            {l === 'en' ? 'EN' : 'हिं'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div lang={hi ? 'hi' : 'en'} className="mt-2 space-y-2.5 text-[14px] leading-relaxed text-lc-mute">
                    {about.map((para, i) => <p key={i}>{para}</p>)}
                  </div>
                </div>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <a href={crafterBook(craftBio.name)} onClick={() => setCraftBio(null)} className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">Book a consultation</a>
                  <span className="label !text-[11px] text-lc-mute">{craftBio.tier} · {craftBio.tier === 'Premium' ? '₹1,100' : '₹3,100'} until 30 Sept</span>
                </div>
              </div>
            )
          })()}
        </Modal>

        {/* View all — one tier's full roster */}
        <Modal open={!!laneTier} onClose={() => setLaneTier(null)} title={laneTier ? `${laneTier} · astrologers` : ''} size="lg">
          {laneTier && (
            <div className="grid gap-3 sm:grid-cols-2">
              {cosmosConsultCrafters.filter((c) => c.tier === laneTier).map((p) => (
                <div key={p.name} className="rounded-[18px] bg-white/5 p-4 ring-1 ring-white/10">
                  <div className="flex items-start gap-3">
                    <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-full bg-white/10 text-lc-accent ring-2 ring-white/20" aria-hidden="true">
                      {p.photo ? (
                        <img src={p.photo} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6Z" /></svg>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <button
                        onClick={() => { setLaneTier(null); setCraftLang('en'); setCraftBio(p) }}
                        className="block max-w-full truncate text-left text-[15px] font-semibold text-white underline-offset-2 hover:underline"
                      >
                        {p.name}
                      </button>
                      <p className="truncate text-[12px] text-lc-mute">{p.focus}</p>
                    </div>
                    <a href={consultTarget} onClick={() => setLaneTier(null)} className="shrink-0 rounded-pill bg-white/10 px-3 py-1.5 text-[12px] font-bold text-white ring-1 ring-white/15 transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100">
                      Book
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>

        {/* price-on-request enquiry (mentors) */}
        <RequestModal open={!!requestFor} onClose={() => setRequestFor(null)} subject={requestFor ?? undefined} theme="dark" />
      </main>

      <SiteFooter dark={dark} />
    </div>
  )
}
